<template>
  <main>
    <SCMItemsFilterComponent
      :loading="loadingRelatedData || loadingTable"
      :categories="categories"
      :subCategories="subCategories"
    />

    <v-container fluid>
      <v-toolbar flat class="main-header mb-6">
        <v-toolbar-title class="main-header__title">
          <h1>{{ $t("heading.items") }}</h1>
        </v-toolbar-title>

        <v-spacer></v-spacer>
        <v-toolbar-items class="main-header__contents">
          <v-btn
            class="my-auto mx-2 radius-50"
            color="primary"
            icon
            :title="$t('changeLocale')"
            @click="changeLocale"
          >
            <v-icon> mdi-web </v-icon>
          </v-btn>

          <v-btn
            v-if="canAdd"
            class="my-auto mx-1 radius-12"
            color="success"
            to="/scm/items/add"
          >
            <v-icon>mdi-plus-circle</v-icon>
            <span class="mx-2"> {{ $t("button.add") }} </span>
          </v-btn>
          <download-excel :data="scmItems" :fields="json_fields">
            <v-btn outlined color="red" large class="mt-3"
              ><v-icon class="mr-3">mdi-cloud-download</v-icon>Export</v-btn
            >
          </download-excel>
        </v-toolbar-items>
      </v-toolbar>
      <hr class="mb-6" />

      <v-data-table
        :headers="scmItemsTableHeaders"
        :loading="loadingTable"
        :items="scmItems"
        :mobile-breakpoint="0"
        :items-per-page="200"
        hide-default-footer
        class="table"
      >
        <template v-slot:item="{ item }">
          <tr class="table__row">
            <td class="table__cell text-center">
              <div style="width: 100px">
                <v-btn
                  icon
                  :to="{ name: 'edititems', params: { id: item.id },query:{...$route.query} }"
                >
                  <v-icon
                    v-if="canEdit"
                    :title="$t('label.editItem')"
                    class="edit-icon"
                    small
                  >
                    mdi-pencil
                  </v-icon>
                </v-btn>

                <v-icon
                  :title="$t('label.previewItem')"
                  class="mx-1"
                  small
                  :disabled="loadingAction"
                  @click="previewSCMItem(item.id)"
                >
                  mdi-eye
                </v-icon>

                <v-icon
                  v-if="canDelete"
                  :title="$t('label.deleteItem')"
                  class="delete-icon"
                  small
                  :disabled="loadingAction"
                  @click="deleteSCMItem(item.id)"
                >
                  mdi-delete
                </v-icon>
              </div>
            </td>
            <td class="table__cell text-center">
              <v-avatar v-if="item.photo" size="50">
                <v-img :src="item ? item.photo : ''"></v-img>
              </v-avatar>
              <span v-else>--</span>
            </td>
            <td class="table__cell text-center">
              <span> {{ item.code || "-" }} </span>
            </td>

            <td class="table__cell name text-center">
              <span> {{ item.name || "-" }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ (item.category && item.category.name) || "-" }} </span>
            </td>

            <td class="table__cell text-center">
              <span>
                {{ (item.sub_category && item.sub_category.name) || "-" }}
              </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.percent_of_safty_stock }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.waiting_days }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.reorder_qty }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.amount_of_purchase }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.min_stock }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.max_stock }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.yearly_needed_qty }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.last_unit_price }} </span>
            </td>

            <td class="table__cell text-center">
              <v-chip small v-if="item.active" color="success">
                {{ $t("label.yes") }}
              </v-chip>
              <v-chip small v-else color="error"> {{ $t("label.no") }} </v-chip>
            </td>

            <td class="table__cell text-center">
              <read-more
                more-str="more"
                less-str="less"
                :text="item.notes || '---'"
                :max-chars="20"
                class="mb-0 major-description m-b-0"
              ></read-more>
            </td>
          </tr>
        </template>
      </v-data-table>
      <!-- pagination -->
      <div class="text-xs-center pt-2">
        <v-pagination
          v-if="pagination.last_page > 1"
          v-model="pagination.current_page"
          :length="pagination.last_page"
          :total-visible="20"
          @input="handlePagination"
        ></v-pagination>
      </div>
      <!-- pagination -->
    </v-container>

    <SCMItemsAddEditDialogComponent
      :itemInfo="scmItemInfo"
      :categories="categories"
      :UOMs="UOMs"
      :loading="loadingAction"
      @saveSCMItem="saveSCMItem"
      @resetItemInfo="resetItemInfo"
      v-model="showAddEditDialog"
    >
      <v-alert v-if="globalServerErrors !== null" dense outlined type="error">
        <p
          class="mb-0"
          v-for="globalErr in globalServerErrors"
          :key="globalErr"
        >
          {{ globalErr[0] }}
        </p>
      </v-alert>
    </SCMItemsAddEditDialogComponent>

    <SCMItemPreviewDialogComponent
      :scmItemPreviewData="scmItemPreviewData"
      :loading="loadingAction"
      :canEdit="canEdit"
      :canDelete="canDelete"
      @edit="showSCMItemEdit"
      @delete="deleteSCMItem"
      v-model="showPreviewDialog"
    />
  </main>
</template>

<script>
import i18n from "@/lang";
import { mapGetters } from "vuex";
import {
  IndexData,
  ShowData,
  StoreData,
  UpdateData,
  DeleteData,
} from "@/helpers/apiMethods";
import { isEqual } from "lodash";
import SCMItemsAddEditDialogComponent from "./components/add-edit-dialog.component";
import SCMItemPreviewDialogComponent from "./components/preview-dialog.component";
import SCMItemsFilterComponent from "./components/scmItemsFilter.component";

export default {
  components: {
    SCMItemsAddEditDialogComponent,
    SCMItemPreviewDialogComponent,
    SCMItemsFilterComponent,
  },
  data() {
    return {
      json_fields: {
        name: "name",
        code: "code",
        category: {
          field: "category.name",
        },
        "sub Category": {
          field: "sub_category.name",
        },
        "Percent Of Safty Stock": {
          field: "percent_of_safty_stock",
        },
        "Waiting Days": {
          field: "waiting_days",
        },
        "Reorder Quantity": {
          field: "reorder_qty",
        },
        "Amount Of Purchase": {
          field: "amount_of_purchase",
        },
        "Min Stock": {
          field: "min_stock",
        },
        "Max Stock": {
          field: "max_stock",
        },
        "Yearly Needed Quantity": {
          field: "yearly_needed_qty",
        },
        "Last Unit Price": {
          field: "last_unit_price",
        },
        Active: {
          field: "active",
        },
        Notes: {
          field: "notes",
        },
      },
      scmItems: [],
      categories: [],
      subCategories: [],
      UOMs: [],
      pagination: {},
      queries: {},
      loadingTable: false,
      loadingRelatedData: false,
      loadingAction: false,
      showAddEditDialog: false,
      showPreviewDialog: false,
      scmItemInfo: {
        category: "",
        sub_category: "",
        unit_of_measure: "",
        code: "",
        name: "",
        yearly_needed_qty: 0,
        percent_of_safty_stock: 0,
        amount_of_purchase: 0,
        min_stock: 0,
        max_stock: 1,
        reorder_qty: 0,
        waiting_days: 0,
        last_unit_price: 0,
        notes: "",
        photo: "",
        active: 1,
      },
      scmItemPreviewData: {},
      globalServerErrors: null,
    };
  },
  async mounted() {
    await this.indexSCMItemsRelatedData();
  },
  computed: {
    ...mapGetters({
      permissions: "permissions",
    }),
    canAdd() {
      return this.permissions.includes("store_api_admin-panel_scm_items");
    },
    canEdit() {
      return this.permissions.includes(
        "update_api_admin-panel_scm_items_{item}"
      );
    },
    canDelete() {
      return this.permissions.includes(
        "destroy_api_admin-panel_scm_items_{item}"
      );
    },
    scmItemsTableHeaders() {
      return [
        {
          text: this.$t("table.actions"),
          align: "center",
          sortable: false,
          width: "110",
        },
        {
          text: this.$t("table.photo"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.itemCode"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.name"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.category"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.subcategory"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.safetyPercent"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.waitingDays"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.reorderQty"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.amountOfPurchace"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.minStock"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.maxStock"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.yearlyNeed"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.lastUnitPrice"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.active"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.note"),
          align: "center",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    changeLocale() {
      i18n.locale = i18n.locale === "en" ? "ar" : "en";
    },
    indexSCMItems(query) {
      this.loadingTable = true;
      IndexData({ reqName: "scm/items", query })
        .then((res) => {
          const { data, meta } = res.data;
          this.scmItems = data;
          this.pagination = meta;
        })
        .catch(() => {
          console.log();
        })
        .finally(() => {
          this.loadingTable = false;
        });
    },
    async indexSCMItemsRelatedData() {
      try {
        this.loadingRelatedData = true;
        const [categoriesRes, subCategoriesRes, UOMRes] = await Promise.all([
          IndexData({ reqName: "scm/categories" }),
          IndexData({ reqName: "scm/sub-categories" }),
          // IndexData({
          //   reqName: "scm/unit-of-measures",
          //   query: { enabled: true },
          // }),
        ]);

        this.categories =
          categoriesRes && categoriesRes.data
            ? categoriesRes.data.data.map((category) => ({
                ...category,
                id: category.id.toString(),
              }))
            : [];
        this.subCategories =
          subCategoriesRes && subCategoriesRes.data
            ? subCategoriesRes.data.data.map((subCategory) => ({
                ...subCategory,
                id: subCategory.id.toString(),
              }))
            : [];
        // this.UOMs = UOMRes && UOMRes.data ? UOMRes.data.data : [];
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingRelatedData = false;
      }
    },
    handlePagination(page) {
      this.queries = { ...this.queries, page };
      if (!isEqual(this.queries, this.$route.query)) {
        this.$router.push({ query: this.queries });
      }
    },
    async showSCMItemEdit(itemId) {
      try {
        this.loadingAction = true;
        const item = await this.fetchSCMItem(itemId);

        this.resetItemInfo();
        this.scmItemInfo = {
          ...item,
          category: (item.category && item.category.id) || "",
          sub_category: (item.sub_category && item.sub_category.id) || "",
          unit_of_measure:
            (item.unit_of_measure && item.unit_of_measure.id) || "",
        };
        this.showPreviewDialog = false;
        this.showAddEditDialog = true;
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingAction = false;
      }
    },
    async previewSCMItem(itemId) {
      try {
        this.loadingAction = true;
        this.scmItemPreviewData = await this.fetchSCMItem(itemId);
        this.showPreviewDialog = true;
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingAction = false;
      }
    },
    async fetchSCMItem(itemId) {
      const res = await ShowData({ reqName: "scm/items", id: itemId });
      return res.data.item;
    },
    saveSCMItem(itemId) {
      this.loadingAction = true;
      itemId ? this.editSCMItem(itemId) : this.addSCMItem();
    },
    addSCMItem() {
      const reqData = { ...this.scmItemInfo };

      reqData.unit_of_measure || delete reqData.unit_of_measure;

      const formData = new FormData();
      Object.keys(reqData).forEach((formItem) => {
        formData.append(formItem, reqData[formItem]);
      });

      StoreData({ reqName: "scm/items", data: formData })
        .then(() => {
          this.showAddEditDialog = false;
          this.indexSCMItems(this.$route.query);
          this.globalServerErrors = null;
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.loadingAction = false;
        })
        .finally(() => {
          this.loadingAction = false;
        });
    },
    editSCMItem(itemId) {
      const reqData = { ...this.scmItemInfo };
      reqData.unit_of_measure || delete reqData.unit_of_measure;
      const formData = new FormData();
      Object.keys(reqData).forEach((formItem) => {
        formData.append(formItem, reqData[formItem]);
      });
      formData.append("_method", "PUT");
      UpdateData({
        reqName: "scm/items",
        data: formData,
        id: itemId,
      })
        .then(() => {
          this.showAddEditDialog = false;
          this.indexSCMItems(this.$route.query);
          this.globalServerErrors = null;
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          // this.loadingAction = false;
        })
        .finally(() => {
          // this.loadingAction = false;
        });
    },
    scrollTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    deleteSCMItem(itemId) {
      this.popUp().then((value) => {
        if (!value.dismiss) {
          this.loadingAction = true;
          DeleteData({ reqName: "scm/items", id: itemId })
            .then(() => {
              this.resetItemInfo();
              this.indexSCMItems(this.$route.query);
            })
            .catch(() => {
              console.log();
            })
            .finally(() => {
              this.loadingAction = false;
            });
        }
      });
    },
    resetItemInfo() {
      this.scmItemInfo = {
        category: "",
        sub_category: "",
        unit_of_measure: "",
        code: "",
        name: "",
        yearly_needed_qty: 0,
        percent_of_safty_stock: 0,
        amount_of_purchase: 0,
        min_stock: 0,
        max_stock: 1,
        reorder_qty: 0,
        waiting_days: 0,
        last_unit_price: 0,
        notes: "",
        photo: "",
        active: 0,
      };
      this.globalServerErrors = null;
    },
  },
  watch: {
    $route: {
      handler(route) {
        const { query } = route;
        this.queries = query;
        this.indexSCMItems(query);
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.v-data-table .table__row td i.mdi-eye {
  color: #212121 !important;
}
</style>
